/**
 * Живая связь поля макета с объектом карты (docs/wayfinding/plan.md §9.1.6).
 *
 * Поле генератора, связанное со столбцом таблицы объектов, хранит в данных указателя не
 * ТЕКСТ, а ССЫЛКУ на строку: `__wfObject:<id объекта>:<снимок значения>`. Поэтому
 * переименование помещения долетает до макета само — значение подставляется в момент
 * сборки, а не в момент выбора.
 *
 * Снимок в ссылке — на случай удалённого объекта: макет должен собраться прежним текстом,
 * а не пустой строкой посреди указателя (решение Паши 16.09). Панель при этом честно
 * говорит, что объекта больше нет.
 *
 * Резолв живёт в shared, потому что данные точки разворачивают трое: форма указателя
 * (превью), выгрузка (недостающие PDF) и «Обновить все макеты». Разные правила у них
 * означали бы разный текст на экране и в печати.
 */
import type { AutocraftFormValue } from './autocraftFields.js';
import { stringifyPropertyValue } from './stringifyPropertyValue.js';

/** Префикс ссылки на объект в значении поля генератора. */
export const AUTOCRAFT_OBJECT_REF_PREFIX = '__wfObject:';

export interface AutocraftObjectRef {
  /** `features.id` объекта. */
  objectId: string;
  /** Значение на момент выбора: им собирается макет, если объекта больше нет. */
  snapshot: string;
}

/**
 * Ссылка строкой. Снимок идёт последним куском и может содержать что угодно, включая
 * двоеточия: разбор режет только по первому после id.
 */
export function autocraftObjectRef(objectId: string, snapshot: string): string {
  return `${AUTOCRAFT_OBJECT_REF_PREFIX}${objectId}:${snapshot}`;
}

/** Ссылка из значения поля; `null` — обычный текст, набранный руками. */
export function readAutocraftObjectRef(value: unknown): AutocraftObjectRef | null {
  if (typeof value !== 'string' || !value.startsWith(AUTOCRAFT_OBJECT_REF_PREFIX)) {
    return null;
  }
  const rest = value.slice(AUTOCRAFT_OBJECT_REF_PREFIX.length);
  const separator = rest.indexOf(':');
  const objectId = separator >= 0 ? rest.slice(0, separator) : rest;
  if (!objectId) {
    return null;
  }
  return { objectId, snapshot: separator >= 0 ? rest.slice(separator + 1) : '' };
}

/** `features.id` — колонка типа uuid: id не той формы Postgres отбивает ошибкой запроса. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Объекты, на которые ссылаются данные точки — сервер по ним вычитывает фичи одним запросом.
 *
 * Отбор по форме uuid именно здесь, а не в `readAutocraftObjectRef`: битый id в базу пускать
 * нельзя (`inArray` по uuid-колонке уронил бы весь запрос и вместе с ним выгрузку), но и
 * ссылку целиком отбрасывать незачем — объекта с таким id просто не найдётся, и поле соберётся
 * снимком из самой ссылки, как у удалённого объекта. Иначе `__wfObject:…` уехало бы в макет
 * сырой строкой.
 */
export function collectAutocraftObjectIds(
  values: Record<string, unknown> | null | undefined,
): string[] {
  const ids = new Set<string>();
  for (const value of Object.values(values ?? {})) {
    const ref = readAutocraftObjectRef(value);
    if (ref && UUID_RE.test(ref.objectId)) {
      ids.add(ref.objectId);
    }
  }
  return [...ids];
}

/** Поле со ссылкой, чей объект не нашёлся: в макет ушёл снимок. */
export interface StaleAutocraftObjectRef {
  fieldKey: string;
  ref: AutocraftObjectRef;
}

export interface ResolvedAutocraftValues {
  values: Record<string, AutocraftFormValue>;
  /** Пусто — все ссылки живые. */
  stale: StaleAutocraftObjectRef[];
}

/**
 * Значения формы со ссылками, развёрнутыми в текущие значения объектов.
 *
 * @param links — связи полей типа указателя: ключ поля генератора → ИМЯ поля объекта. Связь
 *   могли снять уже после того, как ссылку записали, — тогда читать из объекта нечего, и
 *   поле собирается снимком, а не пустотой.
 * @param lookup — свойства объекта по его id; `null` — объекта больше нет.
 */
export function resolveAutocraftObjectValues(
  values: Record<string, AutocraftFormValue>,
  links: Record<string, string> | undefined,
  lookup: (objectId: string) => Record<string, unknown> | null | undefined,
): ResolvedAutocraftValues {
  const out: Record<string, AutocraftFormValue> = {};
  const stale: StaleAutocraftObjectRef[] = [];
  for (const [key, value] of Object.entries(values)) {
    const ref = readAutocraftObjectRef(value);
    if (!ref) {
      out[key] = value;
      continue;
    }
    const column = links?.[key];
    const properties = column ? lookup(ref.objectId) : null;
    if (!properties) {
      out[key] = ref.snapshot;
      // Снятая связь — не потерянный объект: о ней сказать нечего, поле просто застыло.
      if (column) {
        stale.push({ fieldKey: key, ref });
      }
      continue;
    }
    out[key] = stringifyPropertyValue(properties[column!]);
  }
  return { values: out, stale };
}

/**
 * Отпечаток данных, по которым собрана обложка макета: лежит в свойствах стороны
 * (`AUTOCRAFT_COVER_SIGNATURE_PROPERTY`).
 *
 * По нему видно, что обложка отстала от объекта, не ходя за ней в Автокрафт: панель
 * указателя пересобирает её при открытии. «Обновить все макеты» собирает всё подряд и без
 * отпечатка — там меняются не данные, а шаблон в самом Автокрафте, и по данным это не видно;
 * прогон только записывает свежий отпечаток, чтобы открытие указателя следом не гнало
 * лишнюю сборку.
 *
 * Не крипто-хеш: задача — заметить расхождение, а не устоять против подбора. FNV-1a
 * синхронный и одинаковый в браузере и в Node, в отличие от `crypto.subtle`.
 */
export function autocraftLayoutFingerprint(input: {
  generatorHash: string;
  data: Record<string, AutocraftFormValue>;
  pageNumber: number;
}): string {
  // Ключи по алфавиту: порядок в объекте зависит от порядка полей схемы, а он меняется
  // от правки шаблона — отпечаток от этого меняться не должен.
  const stable = Object.keys(input.data)
    .sort()
    .map((key) => `${key}\u0000${stringifyPropertyValue(input.data[key])}`)
    .join('\u0001');
  const source = `${input.generatorHash}\u0002${input.pageNumber}\u0002${stable}`;
  let hash = 0x811c9dc5;
  for (let i = 0; i < source.length; i += 1) {
    hash ^= source.charCodeAt(i);
    // FNV-1a: умножение на простое 16777619 через сдвиги — иначе выходим за 32 бита.
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}
